import type { InputRuleMatch, PasteRuleMatch } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { tagAttributeValue, tagNodeName } from '@/services/forms/tmTiptap/const'
import { getTagsRegexp, tagRegexpGroupName } from '@/services/forms/tmTiptap/utils/getTagsRegexp'

export type InnermostTagMatch = {
  index: number
  text: string
  tag: string
}

export const serializeTagLeaf = (node: ProseMirrorNode): string =>
  node.type.name === tagNodeName ? `{${node.attrs[tagAttributeValue]}}` : ''

export const findInnermostTags = (text: string): InnermostTagMatch[] => {
  const stack: { index: number; hadNested: boolean }[] = []
  const matches: InnermostTagMatch[] = []

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === '{') {
      stack.push({ index: i, hadNested: false })
      continue
    }
    if (char !== '}') {
      continue
    }
    const opened = stack.pop()
    if (!opened) {
      continue
    }
    if (stack.length) {
      stack[stack.length - 1].hadNested = true
    }
    if (opened.hadNested) {
      continue
    }
    const tag = text.slice(opened.index + 1, i)
    if (!tag) {
      continue
    }
    matches.push({
      index: opened.index,
      text: `{${tag}}`,
      tag,
    })
  }

  return matches
}

export const isInsideJsonString = (text: string, position: number): boolean => {
  let inString = false
  let escaped = false
  const limit = Math.min(position, text.length)

  for (let i = 0; i < limit; i++) {
    const char = text[i]
    if (!inString) {
      if (char === '"') {
        inString = true
      }
      continue
    }
    if (escaped) {
      escaped = false
      continue
    }
    if (char === '\\') {
      escaped = true
      continue
    }
    if (char === '"') {
      inString = false
    }
  }

  return inString
}

// JSON objects use the same `{}` as tags — keep only innermost pairs that sit inside a string.
export const findJsonContentTags = (text: string): InnermostTagMatch[] =>
  findInnermostTags(text).filter(
    (match) => isInsideJsonString(text, match.index) && isInsideJsonString(text, match.index + match.text.length),
  )

export const toTagRuleMatch = (text: string, match: InnermostTagMatch): InputRuleMatch => {
  const regexpMatch = getTagsRegexp().exec(match.text)!
  regexpMatch.index = match.index
  regexpMatch.input = text

  return {
    index: match.index,
    text,
    replaceWith: match.text,
    match: regexpMatch,
    data: { groups: regexpMatch.groups },
  }
}

export const toTagPasteRuleMatch = (match: InnermostTagMatch): PasteRuleMatch => ({
  index: match.index,
  text: match.text,
  replaceWith: match.text,
  data: { groups: { [tagRegexpGroupName]: match.tag } },
})
